import React, { RefObject } from 'react';
import {
  Component,
  isFirefox, Position,
  PuzzleTabStrip
} from 'puzzle';
import { TabStrip, TabStripSelectEventArguments, TabStripTab } from '@progress/kendo-react-layout'
import SubmissionStatus from "../Tabs/Status/SubmissionStatus";
import Files from "../Tabs/Files/Files";
import SubmissionDetails from "../Tabs/SubmissionDetails/SubmissionDetails";
import { get, toLower } from "lodash";
import QuotesReview from "../Tabs/QuotesReview/QuotesReview";
import { FILES_TAB_SUBMISSION_DATA } from "../Tabs/Types";
import SupplierFiles from "../../SupplierView/DrawerTabs/SupplierFiles/SupplierFiles";
import SupplementaryFiles from "../Tabs/SupplementaryFiles/SupplementaryFiles";
import classNames from "classnames";
import { showSupplementaryFiles } from "../../util/Util";
import submissionStatus from "../Tabs/Status/SubmissionStatus";

type TABS_MAP = "STATUS" | "DETAILS" | "FILES" | "QUOTES" | "SUPPLEMENTARY_FILES";

type DRAWER_CONTENT_PROPS = {
  data?: any;
  detached?: boolean;
  initializeUserData: any;
  preselectedDrawerTab: number;
  setSubmissionDetailsTabRef?: RefObject<HTMLElement>;
  tabsMap: TABS_MAP[];
}

type DRAWER_CONTENT_STATE = {
  openFromNotes: boolean;
  selected: number;
}

type TABS = {
  [tab in string]: React.ReactElement
}

class DrawerContent extends Component<DRAWER_CONTENT_PROPS, DRAWER_CONTENT_STATE> {
  innerRef: any = React.createRef();
  quotesReviewRef: any = React.createRef();
  filesFilter: any;

  static defaultProps = {
    preselectedDrawerTab: 0
  };

  state = {
    selected: this.props.preselectedDrawerTab,
    openedFromNotes: false
  };

  /**
   * @inheritDoc
   */
  moduleDidMount = () => {
    const {
      detached,
      data,
    } = this.props;

    this.setHeight();
    if (detached) {
      this.dispatch('registerAsDetachedWindow', Position.end);
      this.setWindowTitle(data.submissionId);
    }
    window.addEventListener('resize', this.setHeight.bind(this));
  };

  /**
   * @inheritDoc
   */
  componentDidUpdate = (prevProps: DRAWER_CONTENT_PROPS, prevState: DRAWER_CONTENT_STATE) => {
    const {
      collateralDownloadEnabled,
      quoteStatus,
      submissionId,
      submissionStatus,
      taskStatus,
      taskId
    } = this.props.data;

    if (prevProps.data.submissionId !== submissionId || prevProps.data.taskId !== taskId) {
      const statuses = { taskStatus: taskStatus?.name, submissionStatus: submissionStatus?.name };
      let notAvailable: string[] = [];
      if (!showSupplementaryFiles(statuses, collateralDownloadEnabled)) {
        notAvailable.push('SUPPLEMENTARY_FILES');
      }
      if (!quoteStatus) {
        notAvailable.push('QUOTES');
      }

      const activeTab = this.getSelectedTab();
      if (notAvailable.includes(activeTab)) {
        setTimeout(() => {
          this.setState({
            selected: 0
          })
        }, 0);
      }
    }
    this.props.detached && this.changeDocumentTitle(prevProps.data, prevState);
  };

  /**
   * Checks should title be change and sets
   *
   * @param prevProps
   * @param prevState
   */
  changeDocumentTitle = (
    prevProps: { submissionId: string, taskId: string },
    prevState: { selected: number }
  ): void => {
    const {
      submissionId: prevSubmissionId,
      taskId: prevTaskId
    } = prevProps;
    const {
      submissionId,
      taskId
    } = this.props.data;

    if (prevSubmissionId !== submissionId || prevTaskId !== taskId || prevState.selected !== this.state.selected) {
      this.setWindowTitle(submissionId);
    }
  };

  /**
   * Sets window title on detached drawer window
   *
   * @param submissionId
   */
  setWindowTitle = (submissionId: string): void => {
    const tab = toLower(this.getSelectedTab());
    const tabName = this.translate(tab);

    document.title = this.translate('title', {
      type: this.translate('submission'),
      id: submissionId,
      tabName
    });
  }

  /**
   * returns selected tab
   */
  getSelectedTab = () => this.props.tabsMap[this.state.selected]

  /**
   * Check is change allowed
   */
  handleSelect = (event: TabStripSelectEventArguments, filesFilter: any = {}) => {
    if (this.isAllowedTabChange()) {
      this.changeTab(event, filesFilter, false);
    } else {
      this.dispatch('confirmationQuoteReviewTabChange', '', { tabEvent: event, filesFilter });
    }
  };

  /**
   * Change tab
   */
  changeTab = (event: TabStripSelectEventArguments, filesFilter: any = {}, openedFromNotes: boolean) => {
    this.filesFilter = filesFilter;
    // @ts-ignore
    this.setState({ selected: event.selected, openedFromNotes });
  };

  /**
   * Check is active tab have unsaved changes and if change is allowed
   */
  isAllowedTabChange = () => {
    let changeAllowed = true;
    let { current } = this.quotesReviewRef
    if (current) {
      changeAllowed = !current.isDirty;
    }

    return changeAllowed;
  }

  /**
   * Place tabs in proper order. Using DRAWER_TABS_MAP as an order map
   * @param {TABS} tabs
   */
  placeTabsInOrder = (tabs: TABS) => {
    const { tabsMap } = this.props;

    let tabsInOrder: React.ReactElement[] = [];
    let tabsMapLength = tabsMap.length;
    let i = 0;
    let current;
    while (i < tabsMapLength) {
      current = tabsMap[i];
      tabsInOrder.push(tabs[current]);
      i++;
    }

    return tabsInOrder;
  }

  /**
   * Set height equal to the available free space in the parent element
   */
  setHeight() {
    if (this.innerRef && get(this, 'innerRef.current.reference.current')) {
      const current = get(this, 'innerRef.current.reference.current');
      const parent = current.parentElement;
      const child = current.firstElementChild;
      child.style.height = '100%';
      if (!this.props.detached) {
        child.style.height = `${parent.offsetHeight - 56}px`; // setting height of drawer content - drawer header height - 1px border - 2 * 3px margin
      }
    }
  }

  /**
   * Changes tab on click
   * @param e
   */
  selectTab(e: any) {
    let parentItem = e.parentElement;
    if (parentItem?.getAttribute('role') === "tablist") {
      let index = [...parentItem.children].indexOf(e);
      if (index !== this.state.selected) {
        this.handleSelect({ selected: index });
      }
    }
  }

  /**
   * Returns Suppliers view drawer tabs
   */
  renderSubmissionDrawerContent = () => {
    const {
      partiallyAnalyzed,
      availableTasks,
      projectId,
      submissionId,
      submissionName,
      instructions,
      submissionStatus,
      wordCount,
      quoteStatus,
      tasks,
      sourceLanguage,
      targetLanguage
    } = this.props.data;

    const { userType } = this.props.initializeUserData.userProfile.userInfo;
    let showSupplementaryTab = showSupplementaryFiles({ submissionStatus: submissionStatus?.name })
    let showQuotesTab = !!quoteStatus;

    let tabs: TABS = {
      'DETAILS': (
        <TabStripTab
          key={"submission_details"}
          title={this.translate('details')}
          contentClassName="tp-drawer-body-tab tp-drawer-body-tab-details tp-no-border"
        >
          <SubmissionDetails
            isSupplierView={false}
            actionName={'getAndLoadSubmissionDetails'}
            isDrawerDetached={!!this.props.detached}
            initializeUserData={this.props.initializeUserData}
            shouldNotesBeOpened={this.state.openedFromNotes}
            data={{
              instructions,
              partiallyAnalyzed,
              projectId,
              submissionId,
              submissionName,
              submissionStatus,
              wordCount
            }}
            setSubmissionDetailsTabRef={this.props.setSubmissionDetailsTabRef}
          />
        </TabStripTab>
      ),
      'FILES': (
        <TabStripTab
          key={"submission_files"}
          title={this.translate('files')}
          contentClassName="tp-drawer-body-tab tp-drawer-body-tab-files tp-no-border"
        >
          <Files
            submissionData={{
              availableTasks,
              partiallyAnalyzed,
              projectId,
              submissionId,
              submissionName,
              submissionStatus: submissionStatus?.name,
              tasks,
              sourceLanguage,
              targetLanguage
            } as FILES_TAB_SUBMISSION_DATA}
            initialFilter={this.filesFilter}
            detached={this.props.detached}
            userType={userType}
          />
        </TabStripTab>
      ),
      'QUOTES': (
        <TabStripTab
          key={"submissionDrawer_quotes"}
          disabled={!showQuotesTab}
          title={!showQuotesTab ? null : this.translate('quotes')}
          contentClassName="tp-drawer-body-tab tp-drawer-body-tab-quotes tp-no-border">
          <QuotesReview submissionData={{ submissionId, projectId }} ref={this.quotesReviewRef} />
        </TabStripTab>
      ),
      'SUPPLEMENTARY_FILES': (
        <TabStripTab
          key={"task_supplementaryFiles"}
          disabled={!showSupplementaryTab}
          title={!showSupplementaryTab ? null : this.translate('supplementaryFiles')}
          contentClassName="tp-drawer-body-tab tp-drawer-body-tab-supplementary-files tp-no-border"
        >
          <SupplementaryFiles
            userType={userType}
            isSupplierView={false}
            data={{
              submissionId,
              projectId,
              instructions,
              wordCount,
              submissionStatus,
              partiallyAnalyzed,
              submissionName,
              uploadSubmissionId: submissionId
            }}
            initialFilter={this.filesFilter}
            detached={this.props.detached}
          />
        </TabStripTab>
      ),
      'STATUS': (
        <TabStripTab
          key={"submission_status"}
          title={this.translate('status')}
          contentClassName="tp-drawer-body-tab tp-drawer-body-tab-status tp-no-border"
        >
          <SubmissionStatus submissionData={{ submissionId, projectId, submissionName }}
                            submissionStatus={submissionStatus?.name} />
        </TabStripTab>
      )
    }

    let tabsInOrder = this.placeTabsInOrder(tabs);

    return tabsInOrder;
  };

  /**
   * Returns Client view drawer tabs
   */
  renderTaskDrawerContent = () => {
    const {
      availableEditors = [],
      availableTasks,
      collateralDownloadEnabled,
      instructions,
      parsableState = '',
      partiallyAnalyzed,
      phaseName,
      phaseType,
      projectId,
      sourceLanguage,
      submissionId,
      submissionName,
      targetLanguage,
      taskId,
      taskStatus,
      tasks,
      wordCount,
    } = this.props.data;

    const {
      supplierId,
      userType
    } = this.props.initializeUserData.userProfile.userInfo;

    let showSupplementaryTab = showSupplementaryFiles({
      taskStatus: taskStatus.name,
      submissionStatus: submissionStatus.name
    }, collateralDownloadEnabled);

    let tabs: TABS = {
      'DETAILS': (
        <TabStripTab
          key={"task_details"}
          title={this.translate('details')}
          contentClassName="tp-drawer-body-tab tp-drawer-body-tab-details tp-no-border"
        >
          <SubmissionDetails
            isSupplierView={true}
            actionName={'getTaskDetails'}
            isDrawerDetached={!!this.props.detached}
            initializeUserData={this.props.initializeUserData}
            shouldNotesBeOpened={this.state.openedFromNotes}
            data={{
              submissionId: taskId,
              projectId,
              instructions,
              wordCount,
              submissionStatus: taskStatus,
              partiallyAnalyzed,
              phaseType,
              submissionName,
              submissionIdWordCount: submissionId
            }}
            setSubmissionDetailsTabRef={this.props.setSubmissionDetailsTabRef}
          />
        </TabStripTab>
      ),
      'FILES': (
        <TabStripTab
          key={"task_files"}
          title={this.translate('files')}
          contentClassName="tp-drawer-body-tab tp-drawer-body-tab-files tp-no-border"
        >
          <SupplierFiles
            isSupplierView={true}
            submissionData={{
              availableEditors,
              availableTasks,
              partiallyAnalyzed,
              projectId,
              submissionId,
              submissionName,
              submissionStatus: taskStatus.name,
              supplierId,
              tasks,
              taskId,
              parsableState,
              phaseName,
              phaseType,
              sourceLanguage,
              targetLanguage
            } as FILES_TAB_SUBMISSION_DATA}
            initialFilter={this.filesFilter}
            detached={!!this.props.detached}
            userType={userType}
          />
        </TabStripTab>
      ),
      'SUPPLEMENTARY_FILES': (
        <TabStripTab
          key={"task_supplementaryFiles"}
          disabled={!showSupplementaryTab}
          title={!showSupplementaryTab ? null : this.translate('supplementaryFiles')}
          contentClassName="tp-drawer-body-tab tp-drawer-body-tab-supplementary-files tp-no-border"
        >
          <SupplementaryFiles
            userType={userType}
            isSupplierView={true}
            data={{
              submissionId: taskId,
              projectId,
              instructions,
              wordCount,
              taskStatus,
              partiallyAnalyzed,
              submissionName,
              uploadSubmissionId: submissionId
            }}
            initialFilter={this.filesFilter}
            detached={this.props.detached}
          />
        </TabStripTab>
      )
    }

    let tabsInOrder = this.placeTabsInOrder(tabs);

    return tabsInOrder
  };

  render() {
    const {
      taskId = undefined,
    } = this.props.data;
    const {
      selected
    } = this.state;
    let classes = classNames(
      'tp-drawer-content',
      {
        'tp-drawer-detached': !!this.props.detached,
        'tp-supplier-view-drawer': taskId
      }
    )
    let tabs = taskId ? this.renderTaskDrawerContent() : this.renderSubmissionDrawerContent()

    return (
      <PuzzleTabStrip
        className={`${classes} ${isFirefox() ? 'tp-firefox' : ''}`}
        ref={this.innerRef}
        translate={this.translate}
        handleSelect={this.handleSelect}
        tabCount={tabs.length}
        selected={selected}
      >
        <TabStrip
          selected={this.state.selected}
        >
          {tabs}
        </TabStrip>
      </PuzzleTabStrip>
    );
  }
}

export default DrawerContent;
